// client/src/utils/dateUtils.ts (converted from .js, M4-TS proof conversion)
//
// Display formatting for timestamps. Source/weighbridge timestamps arrive from
// the API as `+02:00` strings and app timestamps as UTC `Z` strings; the
// `formatMining*` helpers pin Africa/Johannesburg, the `formatApp*` helpers use
// the viewer's timezone. `toApiDateString` turns a picker Date into the
// `YYYY-MM-DD` the API expects (local calendar date, never UTC-shifted).

const MINING_TZ = 'Africa/Johannesburg';

/** Anything `new Date()` accepts; null/undefined/'' format as ''. */
export type DateInput = string | number | Date | null | undefined;

export function formatMiningDate(
  dateStr: DateInput,
  opts: Intl.DateTimeFormatOptions = {}
): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-ZA', {
    timeZone: MINING_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...opts,
  });
}

export function formatMiningDateTime(dateStr: DateInput): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString('en-ZA', {
    timeZone: MINING_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export function formatMiningDateShort(dateStr: DateInput): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-ZA', {
    timeZone: MINING_TZ,
    day: '2-digit',
    month: 'short',
  });
}

export function formatMiningTime(dateStr: DateInput): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-ZA', {
    timeZone: MINING_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function formatMiningDateLong(dateStr: DateInput): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-ZA', {
    timeZone: MINING_TZ,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatAppDate(dateStr: DateInput, opts: Intl.DateTimeFormatOptions = {}): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...opts,
  });
}

export function formatAppDateTime(dateStr: DateInput): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString('en-ZA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function formatRelativeTime(dateStr: DateInput): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  const now = Date.now();
  const diffMs = now - date.getTime();
  if (diffMs < 0) return 'just now';
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatAppDate(dateStr);
}

export function toApiDateString(date: DateInput): string {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function isSASTDate(dateStr: unknown): boolean {
  if (typeof dateStr !== 'string') return false;
  return dateStr.endsWith('+02:00');
}

export { MINING_TZ };
